import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import client from '../api/client'

const FEATURES = [
  { icon: '💬', title: 'AI 상담', desc: '고민이 있을 때 털어놓으세요', badge: '🔥 인기상품', path: '/counseling' },
  { icon: '🍀', title: '오늘의 행운', desc: '행운 색상·숫자·방향·음식', badge: '📅 매일갱신', path: '/features/lucky-today' },
  { icon: '🍽️', title: '오늘 뭐 먹지?', desc: '사주로 보는 오늘의 메뉴', badge: '📅 매일갱신', path: '/features/food-today' },
  { icon: '💑', title: '궁합 분석', desc: '우리 잘 맞을까요?', badge: null, path: '/features/compatibility' },
  { icon: '👥', title: '그룹 궁합', desc: '2~5인 그룹 사주 궁합', badge: null, path: '/features/group-compatibility' },
  { icon: '📜', title: '신년운세', desc: '연간 총평·분야별·월별 운세', badge: null, path: '/features/yearly-report' },
  { icon: '🌊', title: '대운 분석', desc: '10년 주기 대운 흐름', badge: null, path: '/features/daeun' },
  { icon: '📅', title: '택일(擇日)', desc: '나에게 맞는 길일 찾기', badge: null, path: '/features/lucky-date' },
  { icon: '🌙', title: '꿈 해몽', desc: '사주로 풀어보는 꿈의 의미', badge: null, path: '/features/dream-interpretation' },
  { icon: '✍️', title: '이름 풀이', desc: '성명학 분석 + 작명 추천', badge: null, path: '/features/name-analysis' },
  { icon: '👤', title: '유명인 비교', desc: '나랑 사주가 닮은 사람은?', badge: null, path: '/features/celebrity-match' },
  { icon: '💼', title: '직업 추천', desc: '내 팔자에 맞는 직업은?', badge: null, path: '/features/job-recommend' },
  { icon: '🎴', title: '캐릭터 카드', desc: '나만의 사주 카드 만들기', badge: null, path: '/features/character-card' },
]


interface ReviewItem {
  quote: string
  stars: number
  date: string
}

interface VisitStats {
  total_users: number
  visits: { total: number; today: number }
  unique_visitors: { total: number; today: number }
}

export default function Landing() {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const [dailyGan, setDailyGan] = useState('')
  const [dailyJi, setDailyJi] = useState('')
  const [stats, setStats] = useState<VisitStats | null>(null)
  const [reviews, setReviews] = useState<ReviewItem[] | null>(null)
  const heroBgRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    client.get('/saju/daily').then((r) => {
      setDailyGan(r.data.day_gan || '')
      setDailyJi(r.data.day_ji || '')
    }).catch(() => {})
  }, [])

  const visitRecordedRef = useRef(false)

  useEffect(() => {
    client.get('/stats/visits').then((r) => setStats(r.data)).catch(() => {})
    if (!visitRecordedRef.current) {
      visitRecordedRef.current = true
      client.post('/stats/visit').catch(() => {})
    }
  }, [])

  useEffect(() => {
    client.get('/reviews/landing').then((r) => {
      const data = r.data as { id: number; rating: number; content: string; created_at: string }[]
      if (data.length > 0) {
        setReviews(data.map((rv) => ({
          quote: rv.content,
          stars: rv.rating,
          date: rv.created_at.slice(0, 10).replace(/-/g, '.'),
        })))
      }
    }).catch(() => {})
  }, [])


  // 패럴랙스 — 히어로 배경 문자
  useEffect(() => {
    const handleScroll = () => {
      if (heroBgRef.current) {
        heroBgRef.current.style.transform = `translateY(${window.scrollY * 0.25}px)`
      }
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // 스크롤 fade-in
  useEffect(() => {
    const els = document.querySelectorAll('[data-fade]')
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('is-visible')
          observer.unobserve(e.target)
        }
      }),
      { threshold: 0.12 }
    )
    els.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [reviews])

  return (
    <div className="min-h-screen bg-[var(--color-bg)] text-[var(--color-text-primary)]">
      {/* 헤더 */}
      <header className="flex items-center justify-between px-6 sm:px-10 py-4 border-b border-[var(--color-border-default)]">
        <div>
          <span className="text-[var(--color-accent)] font-bold text-lg">🏪 운명편의점</span>
          <p className="text-[var(--color-text-disabled)] text-xs">365일 24시간 · 연중무휴</p>
        </div>
        <div className="flex gap-2">
          {isAuthenticated ? (
            <button onClick={() => navigate('/dashboard')}
              className="px-4 py-2 bg-[var(--color-accent)] hover:opacity-90 rounded-xl text-[var(--color-primary)] text-sm font-bold transition-opacity">
              입장하기
            </button>
          ) : (
            <>
              <button onClick={() => navigate('/login')}
                className="px-4 py-2 border border-[var(--color-border-default)] hover:border-[var(--color-border-accent)] rounded-xl text-[var(--color-text-secondary)] text-sm transition-colors">
                로그인
              </button>
              <button onClick={() => navigate('/register')}
                className="px-4 py-2 bg-[var(--color-accent)] hover:opacity-90 rounded-xl text-[var(--color-primary)] text-sm font-bold transition-opacity">
                단골 등록
              </button>
            </>
          )}
        </div>
      </header>

      {/* SECTION 1 — 히어로 */}
      <section className="flex flex-col items-center justify-center text-center px-6 pt-20 pb-16 relative overflow-hidden">
        {/* 배경 문양 — 패럴랙스 */}
        <div ref={heroBgRef} className="absolute inset-0 opacity-5 select-none pointer-events-none text-[8rem] sm:text-[10rem] flex flex-wrap gap-4 overflow-hidden leading-none text-[var(--color-accent)] will-change-transform">
          {['甲','乙','丙','丁','戊','己','庚','辛','壬','癸','子','丑','寅','卯','辰','巳','午','未','申','酉','戌','亥'].map((c,i) => (
            <span key={i}>{c}</span>
          ))}
        </div>

        {/* 네온 간판 */}
        <div className="relative mb-8 inline-block">
          <div className="border-2 border-[var(--color-accent)]/60 rounded-2xl px-6 sm:px-10 py-6 bg-[var(--color-surface)]/80 backdrop-blur-sm shadow-[0_0_30px_rgba(247,214,103,0.25)]">
            <p className="text-5xl mb-3 neon-pulse">🏪</p>
            <h1 className="text-3xl sm:text-4xl font-black text-[var(--color-accent)] tracking-tight neon-text mb-1">운명편의점</h1>
            <div className="text-[var(--color-accent)]/60 text-xs mb-3">━━━━━━━━━━━━━━━━━━</div>
            <p className="text-[var(--color-text-primary)]/90 text-lg font-medium leading-relaxed">
              24시간 영업 중<br />
              당신의 팔자,<br />
              편하게 들러서 보고 가세요.
            </p>
            <div className="text-[var(--color-accent)]/60 text-xs mt-3">━━━━━━━━━━━━━━━━━━</div>
            <p className="text-[var(--color-text-tertiary)] text-sm mt-2">사주 × MBTI × 혈액형</p>

            {/* OPEN 사인 */}
            <div className="absolute -top-3 -right-3 bg-[var(--color-success)] text-white text-xs font-bold px-2 py-1 rounded-lg animate-pulse shadow-lg">
              OPEN
            </div>
          </div>
        </div>

        <p className="text-[var(--color-text-tertiary)] text-lg italic mb-8">"어차피 내 팔자, 알고나 삽시다."</p>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => navigate(isAuthenticated ? '/dashboard' : '/register')}
            className="px-8 py-4 bg-[var(--color-accent)] hover:opacity-90 rounded-2xl text-[var(--color-primary)] text-lg font-bold transition-opacity shadow-lg">
            🚪 입장하기
          </button>
          <a href="#features"
            className="px-8 py-4 border border-[var(--color-border-default)] hover:border-[var(--color-border-accent)] rounded-2xl text-[var(--color-text-secondary)] text-lg transition-colors">
            잠깐 구경만요 →
          </a>
        </div>
      </section>

      {/* SECTION 1.5 — 방문 통계 */}
      {stats && (
        <section className="px-6 sm:px-10 py-4 bg-[var(--color-bg)] border-b border-[var(--color-border-default)]">
          <div className="max-w-2xl mx-auto flex flex-wrap justify-center gap-6 sm:gap-10">
            <div className="text-center">
              <p className="text-[var(--color-accent)] text-xl font-bold">{stats.total_users.toLocaleString()}</p>
              <p className="text-[var(--color-text-tertiary)] text-xs mt-0.5">👥 누적 단골</p>
            </div>
            <div className="text-center">
              <p className="text-[var(--color-accent)] text-xl font-bold">{stats.visits.total.toLocaleString()}</p>
              <p className="text-[var(--color-text-tertiary)] text-xs mt-0.5">🔮 누적 방문</p>
            </div>
            <div className="text-center">
              <p className="text-[var(--color-success)] text-xl font-bold">{stats.visits.today.toLocaleString()}</p>
              <p className="text-[var(--color-text-tertiary)] text-xs mt-0.5">📌 오늘 방문</p>
            </div>
          </div>
        </section>
      )}

      {/* SECTION 2 — 오늘의 일진 미리보기 */}
      <section className="px-6 sm:px-10 py-12 bg-[var(--color-bg)] border-y border-[var(--color-border-default)]">
        <div className="max-w-xl mx-auto">
          <div data-fade className="bg-[var(--color-surface)] border border-[var(--color-accent)]/20 rounded-2xl p-6">
            <p className="text-[var(--color-text-tertiary)] text-sm mb-1">📅 오늘은</p>
            <p className="text-[var(--color-text-primary)] text-2xl font-bold mb-1">
              {dailyGan && dailyJi ? `${dailyGan}${dailyJi}일` : '오늘의 일진'}
            </p>
            <p className="text-[var(--color-accent)]/70 text-sm mb-4">오늘의 기운: 새로운 시작, 추진력</p>

            <div className="space-y-2 mb-4">
              {[
                { label: '🎨 행운 색상', blur: true },
                { label: '🔢 행운 숫자', blur: true },
                { label: '💬 오늘의 조언', blur: true },
              ].map((item) => (
                <div key={item.label} className="flex items-center justify-between py-2 border-b border-[var(--color-border-default)]">
                  <span className="text-[var(--color-text-tertiary)] text-sm">{item.label}</span>
                  <span className="blur-sm select-none text-[var(--color-text-secondary)] text-sm">●●●●●</span>
                </div>
              ))}
            </div>

            <div className="bg-[var(--color-accent)]/10 border border-[var(--color-accent)]/20 rounded-xl p-3 text-center">
              <p className="text-[var(--color-accent)] text-sm mb-2">👉 로그인하면 내 사주 기반 맞춤 운세를 볼 수 있어요</p>
              <button onClick={() => navigate(isAuthenticated ? '/features/lucky-today' : '/login')}
                className="px-4 py-2 bg-[var(--color-accent)] hover:opacity-90 rounded-lg text-[var(--color-primary)] text-sm font-bold transition-opacity">
                내 운세 보러가기
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3 — 기능 진열대 */}
      <section id="features" className="px-6 sm:px-10 py-16">
        <div className="max-w-4xl mx-auto">
          <div data-fade>
            <h2 className="text-2xl font-bold text-[var(--color-text-primary)] text-center mb-2">🛒 이런 거 팔아요</h2>
            <p className="text-[var(--color-text-tertiary)] text-sm text-center mb-10">운명편의점의 특선 상품</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map((f, idx) => (
              <button key={f.title} data-fade onClick={() => navigate(isAuthenticated ? f.path : '/register')}
                style={{ transitionDelay: `${idx * 60}ms` }}
                className="relative bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-5 text-left hover:border-[var(--color-border-accent)] hover:shadow-lg hover:-translate-y-0.5 transition-all">
                {f.badge && (
                  <span className="absolute -top-2 -right-2 bg-[var(--color-accent)] text-[var(--color-primary)] text-xs font-bold px-2 py-0.5 rounded-full">
                    {f.badge}
                  </span>
                )}
                <p className="text-3xl mb-3">{f.icon}</p>
                <h3 className="text-[var(--color-text-primary)] font-semibold mb-1">{f.title}</h3>
                <p className="text-[var(--color-text-tertiary)] text-sm">{f.desc}</p>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4 — 영수증 후기 */}

      {reviews && <section className="px-6 sm:px-10 py-16 bg-[var(--color-bg)] border-y border-[var(--color-border-default)]">
        <div className="max-w-4xl mx-auto">
          <div data-fade>
            <h2 className="text-2xl font-bold text-[var(--color-text-primary)] text-center mb-2">📋 단골 손님 후기</h2>
            <p className="text-[var(--color-text-tertiary)] text-sm text-center mb-10">영수증에 남겨주셨어요</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {reviews.map((r, i) => (
              <div key={i} data-fade style={{ transitionDelay: `${i * 80}ms` }}
                className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-xl p-5 font-mono">
                <div className="text-center text-[var(--color-accent)] font-bold mb-2">🏪 운명편의점</div>
                <div className="border-t border-dashed border-[var(--color-border-default)] my-2" />
                <div className="text-[var(--color-text-tertiary)] text-xs mb-2">구매일: {r.date}</div>
                <div className="border-t border-dashed border-[var(--color-border-default)] my-2" />
                <p className="text-[var(--color-text-secondary)] text-sm italic mb-2">"{r.quote}"</p>
                <div className="border-t border-dashed border-[var(--color-border-default)] my-2" />
                <div className="flex justify-between items-center">
                  <span className="text-[var(--color-accent)] text-sm">{'★'.repeat(r.stars)}{'☆'.repeat(5-r.stars)}</span>
                  <span className="text-[var(--color-text-tertiary)] text-xs">또 방문할게요 👋</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>}

      {/* SECTION 5 — CTA */}
      <section className="px-6 sm:px-10 py-14 sm:py-20 text-center">
        <div data-fade className="max-w-lg mx-auto">
          <p className="text-4xl mb-4">🏪</p>
          <h2 className="text-2xl font-bold text-[var(--color-text-primary)] mb-3">아직 단골 손님이 아니신가요?</h2>
          <p className="text-[var(--color-text-tertiary)] mb-6">
            사주 + MBTI + 혈액형으로<br />나만의 운세를 받아보세요.
          </p>
          <div className="text-left inline-block mb-8 bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-xl p-5">
            <p className="text-[var(--color-text-tertiary)] text-sm mb-3">가입하면 바로 드려요:</p>
            <ul className="space-y-2 text-sm text-[var(--color-text-secondary)]">
              <li>✅ 내 사주 캐릭터 카드</li>
              <li>✅ 오늘의 맞춤 운세</li>
              <li>✅ AI 상담 무료 이용</li>
            </ul>
          </div>
          <div>
            <button onClick={() => navigate('/register')}
              className="px-8 py-4 bg-[var(--color-accent)] hover:opacity-90 rounded-2xl text-[var(--color-primary)] text-lg font-bold transition-opacity shadow-lg block w-full sm:w-auto sm:inline-block">
              단골 등록하러 가기 🚪
            </button>
            {!isAuthenticated && (
              <p className="text-[var(--color-text-disabled)] text-sm mt-4">
                이미 단골이세요?{' '}
                <button onClick={() => navigate('/login')} className="text-[var(--color-accent)] hover:underline">로그인</button>
              </p>
            )}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="text-center px-6 py-8 border-t border-[var(--color-border-default)]">
        <p className="text-[var(--color-accent)] font-bold mb-1">🏪 운명편의점</p>
        <p className="text-[var(--color-text-tertiary)] text-sm mb-3">내 운명의 해답을 가볍게 픽업하는 곳, AI 맞춤 상담 플랫폼</p>
        <p className="text-[var(--color-text-disabled)] text-xs italic mb-3">"당신의 팔자, 저희가 잠깐 봐드릴게요."</p>
        <p className="text-[var(--color-text-disabled)] text-xs">© 2026 운명편의점. All rights reserved.</p>
      </footer>

      <style>{`
        .neon-text {
          text-shadow: 0 0 10px rgba(247,214,103,0.8), 0 0 20px rgba(247,214,103,0.4);
        }
        .neon-pulse {
          animation: neonPulse 2s ease-in-out infinite;
        }
        @keyframes neonPulse {
          0%, 100% { filter: drop-shadow(0 0 8px rgba(247,214,103,0.8)); }
          50% { filter: drop-shadow(0 0 2px rgba(247,214,103,0.2)); }
        }
        [data-fade] {
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.55s ease, transform 0.55s ease;
        }
        [data-fade].is-visible {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>
    </div>
  )
}
